import { useEffect, useState } from "react";
import { Loader2, ListChecks } from "lucide-react";
import { toast } from "sonner";

import { runService } from "@/services/runService";
import { TASK_CATEGORIES, TASK_PRIORITIES, RECURRENCE_RULES } from "@/config/runOptions";
import { Field } from "@/components/profile/FormControls";
import { RelatedEntityPicker } from "@/components/run/RelatedEntityPicker";
import { toLocalInput } from "@/lib/datetime";
import { parseFieldErrors } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const empty = { title: "", description: "", category: "administrative", priority: "normal", due_at: "", is_recurring: false, recurrence_rule: "weekly" };

/** Create or edit a task. `presetRelated` locks the linked record; pass `task` to edit. */
export const NewTaskDialog = ({ open, onOpenChange, presetRelated, task, onCreated }) => {
  const [form, setForm] = useState(empty);
  const [related, setRelated] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const editing = !!task?.id;

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        title: task.title || "", description: task.description || "", category: task.category || "administrative",
        priority: task.priority || "normal", due_at: task.due_at ? toLocalInput(task.due_at) : "",
        is_recurring: !!task.is_recurring, recurrence_rule: task.recurrence_rule || "weekly",
      });
      setRelated(task.related?.id ? task.related : null);
    } else {
      setForm(empty);
      setRelated(presetRelated || null);
    }
    setErrors({});
  }, [open, task, editing, presetRelated]);

  const save = async () => {
    setErrors({}); setSaving(true);
    try {
      const payload = {
        title: form.title,
        description: form.description || null,
        category: form.category,
        priority: form.priority,
        due_at: form.due_at ? new Date(form.due_at).toISOString() : null,
        is_recurring: form.is_recurring,
        recurrence_rule: form.is_recurring ? form.recurrence_rule : null,
        related_entity_type: related?.type || null,
        related_entity_id: related?.id || null,
      };
      if (editing) await runService.updateTask(task.id, payload);
      else await runService.createTask(payload);
      toast.success(editing ? "Task updated" : "Task created");
      onCreated?.();
      onOpenChange(false);
    } catch (err) {
      setErrors(parseFieldErrors(err));
      toast.error(err.message || "Please correct the highlighted fields");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="new-task-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ListChecks className="h-5 w-5 text-accent" /> {editing ? "Edit Task" : "New Task"}</DialogTitle>
          <DialogDescription>Track something you don't want to forget across your business.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Field label="Title" htmlFor="task-title" error={errors.title}>
            <Input id="task-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Call escrow officer" data-testid="task-title" />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Category" htmlFor="task-category">
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger id="task-category" data-testid="task-category"><SelectValue /></SelectTrigger>
                <SelectContent>{TASK_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Priority" htmlFor="task-priority">
              <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger id="task-priority" data-testid="task-priority"><SelectValue /></SelectTrigger>
                <SelectContent>{TASK_PRIORITIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Due date & time (optional)" htmlFor="task-due">
            <Input id="task-due" type="datetime-local" value={form.due_at} onChange={(e) => set("due_at", e.target.value)} data-testid="task-due" />
          </Field>
          <Field label="Link to (optional)"><RelatedEntityPicker value={related} onChange={setRelated} locked={!!presetRelated} /></Field>
          <Field label="Details (optional)" htmlFor="task-desc" error={errors.description}>
            <Textarea id="task-desc" rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="task-desc" />
          </Field>
          <div className="flex items-center gap-3">
            <Checkbox id="task-recurring" checked={form.is_recurring} onCheckedChange={(v) => set("is_recurring", !!v)} data-testid="task-recurring" />
            <label htmlFor="task-recurring" className="text-sm">Repeat this task</label>
            {form.is_recurring && (
              <Select value={form.recurrence_rule} onValueChange={(v) => set("recurrence_rule", v)}>
                <SelectTrigger className="ml-auto w-[130px]" data-testid="task-recurrence"><SelectValue /></SelectTrigger>
                <SelectContent>{RECURRENCE_RULES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>
          {form.is_recurring && <p className="text-xs text-muted-foreground">A new task is created automatically each time you complete this one.</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="new-task-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="new-task-save">
            {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : editing ? "Save Changes" : "Create Task"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
